import { useState, useCallback } from "react";
import { useQuery } from "react-query";
import * as s from "styles/ModalCommonStyled";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import TextArea from "components/atoms/input/TextArea";
import BigButton from "components/atoms/buttons/BigButton";

import {
  BasePagination,
  SORT_DIRECTION,
  Validate,
} from "api/interfaces/commonInterface.interface";
import {
  AddUserDto,
  UserDto,
  UserGroupInfo,
} from "api/interfaces/userInterface.interface";
import { getGroupListForEdit } from "api/uGroupAPI";
import type { Option, UserGroupSelectOption } from "utils/options";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";

import { checkEmailRegEx } from "utils/regEx";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import { ValidateWord } from "components/atoms/text/Text";
import Spinner from "components/atoms/loader/Spinner";
import styled from "styled-components";
import UserGroupSelect from "pages/users/userList/add/UserGroupSelect";
import useApiError from "hook/useApiError";
import { checkEmpty, toTrim } from "utils/functions";

interface Props {
  onModal: (type?: string) => void;
  onClickSave: (userDto: AddUserDto, update?: boolean) => void;
  selectedUser: UserDto | undefined;
  onClickDeactive?: (userId: string) => void;
  isLoading: boolean;
}

function AddUser(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { intlString, lang } = useApiError();

  const [selectGroup, setSelectGroup] = useState<string[]>([]);

  const [validate, setValidate] = useState<Validate>({
    // lastName: false,
    // firstName: false,
    email: false,
    group: false,
  });
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    // lastName: "",
    // firstName: "",
    email: "",
    group: "",
  });

  const [userDto, setUserInfo] = useState<AddUserDto>({
    userId: "",
    // name: "",
    lastName: "",
    firstName: "",
    email: "",
    description: "",
    userGroups: [],
  });

  const onCheckEmail = useCallback((email: string) => {
    setValidate((info) => {
      return { ...info, email: !checkEmailRegEx(toTrim(email)) };
    });
    setValidateMsg((info) => {
      return {
        ...info,
        email: !checkEmailRegEx(toTrim(email))
          ? intlString(lang.validateMsg.user.email)
          : "",
      };
    });
    return checkEmailRegEx(toTrim(email));
  }, []);

  const onChangeUserInfo = useCallback((e: React.ChangeEvent<any>) => {
    let {
      target: { name, value },
    } = e;
    if (name === "email") {
      // level 설정 client/server 상위 선택못하게 막아야 함
      setUserInfo((info) => {
        return { ...info, email: toTrim(value) };
      });
    } else {
      setUserInfo((info) => {
        return { ...info, [name]: value as string };
      });
    }
  }, []);

  const onChangeUserGroup = useCallback((value: string[]) => {
    setUserInfo((info) => {
      const userGroups = (value || []).map((groupId) => ({
        groupId: String(groupId),
      })) as UserGroupInfo[];
      return { ...info, userGroups };
    });
    setSelectGroup(value || []);
  }, []);

  const queryInfo: BasePagination = {
    total: 0,
    pageNum: 0,
    pageLimit: 1000,
    keyword: "",
    sortType: "name",
    sortDirection: SORT_DIRECTION.ASC,
  };

  const [groups, setGroups] = useState<UserGroupSelectOption[]>([]);
  const { error, isFetching: groupIsFetching } = useQuery(
    ["group", queryInfo],
    () =>
      getGroupListForEdit({
        payload: queryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (!res.result) {
          return;
        }
        setGroups(
          res.result.map((group: any) => {
            const { groupId, name, enableEdit, userPermission } = group;
            return {
              value: groupId as string,
              label: name,
              disabled:
                !enableEdit ||
                !selectedAccount.userPermission.manageUsersAndGroups,
              userPermission,
            };
          })
        );

        if (userDto.userGroups !== undefined) {
          const idList = userDto.userGroups.map((group) => group.groupId);

          setSelectGroup(idList.map((id) => String(id)));
        }
      },
      onError: (e: any) => {
        console.info(e);
      },
    }
  );

  const onSaveBefore = useCallback(() => {
    if (!onCheckEmail(userDto.email)) {
      return;
    }
    setValidate((info) => {
      return {
        ...info,
        // lastName: false,
        // firstName: false,
        email: false,
        group: false,
        companyPhone: false,
        description: false,
      };
    });
    setValidateMsg((info) => {
      return {
        ...info,
        // lastName: "",
        // firstName: "",
        email: "",
        group: "",
        companyPhone: "",
        description: "",
      };
    });
    let info: AddUserDto = {
      ...userDto,
      email: toTrim(userDto.email),
      firstName: toTrim(userDto.firstName),
      lastName: toTrim(userDto.lastName),
      description: toTrim(userDto.description),
    };
    setUserInfo(info);

    let checkedFlag = false;

    if (!checkEmailRegEx(info.email)) {
      checkedFlag = true;
      setValidate((info) => {
        return { ...info, email: true };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          email: intlString(lang.validateMsg.user.email),
        };
      });
    }

    if (userDto.userGroups === undefined || userDto.userGroups.length === 0) {
      checkedFlag = true;
      setValidate((info) => {
        return { ...info, group: true };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          group: intlString(lang.validateMsg.user.group.selected),
        };
      });
    }

    if (!checkEmpty(userDto.description) && userDto.description.length > 256) {
      setValidate((info) => {
        return { ...info, description: false };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          description: intlString(lang.validateMsg.profile.description),
        };
      });
      return;
    }
    if (checkedFlag) {
      return;
    }

    props.onClickSave(info, false);
  }, [props, userDto]);

  return (
    <BigPopupTemplate title="Add User" onModal={() => props.onModal("add")}>
      <Inner>
        {props.isLoading && (
          <s.LoadingWrapper>
            <Spinner />
          </s.LoadingWrapper>
        )}

        <s.Row>
          <ModalInput label="Email (ID)" essential half>
            <Input
              name="email"
              autoComplete="off"
              type="search"
              placeholder="Please enter an email"
              pattern="[A-Za-z]+"
              maxLength={100}
              value={userDto.email}
              onChange={onChangeUserInfo}
              onBlur={() => onCheckEmail(userDto.email)}
              isValidated={validate.email}
              data-testid="users-user-list-add-email-change-user-info"
            />

            <ValidateWord data-testid="users-add-validate-email">
              {validateMsg.email}
            </ValidateWord>
          </ModalInput>
          <ModalInput label="User Groups" essential half>
            <UserGroupSelect
              options={groups}
              loading={groupIsFetching}
              value={selectGroup}
              onChange={(value) => onChangeUserGroup(value)}
            />

            <ValidateWord data-testid="users-add-validate-group">
              {validateMsg.group}
            </ValidateWord>
          </ModalInput>
        </s.Row>
        <s.Row>
          <ModalInput label="First Name">
            <Input
              name="firstName"
              value={userDto.firstName}
              placeholder="Please enter a first name"
              maxLength={100}
              onChange={onChangeUserInfo}
              // isValidated={validate.firstName}
              data-testid="users-user-list-add-first-name-change-user-info"
            />
            {/* <ValidateWord>{validateMsg.firstName}</ValidateWord> */}
          </ModalInput>
          <ModalInput label="Last Name">
            <Input
              name="lastName"
              value={userDto.lastName}
              placeholder="Please enter a last name"
              maxLength={100}
              onChange={onChangeUserInfo}
              // isValidated={validate.lastName}
              data-testid="users-user-list-add-last-name-change-user-info"
            />
            {/* <ValidateWord>{validateMsg.lastName}</ValidateWord> */}
          </ModalInput>
        </s.Row>

        <ModalInput label="Description" isValidated={validate.description}>
          <TextArea
            name="description"
            value={userDto.description}
            placeholder="Please enter a description"
            maxLength={200}
            onChange={onChangeUserInfo}
            data-testid="users-user-list-add-description-change-user-info"
          />

          <ValidateWord>{validateMsg.description}</ValidateWord>
        </ModalInput>

        <s.ButtonWrapper>
          <BigButton
            label="Save"
            onClickBtn={onSaveBefore}
            data-testid="users-user-list-add-button-save-before"
          />
        </s.ButtonWrapper>
      </Inner>
    </BigPopupTemplate>
  );
}

export default AddUser;

const Inner = styled.div`
  position: relative;
  width: 100%;
`;
